import React, { useState } from "react";
import { CounterpartyAddress } from "@common/schemas/counterpartyAddress.schema";
import { CounterpartyContact } from "@common/schemas/counterpartyContact.schema";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import {
    formatCounterpartyAddressLines,
    formatCounterpartyContactHeadline,
    formatCounterpartyContactName,
} from "@components/counterparty/counterpartyBookFormat";
import styles from "./SelectCustomerBooksModal.module.scss";

export interface SelectedCustomerBooks {
    contact: CounterpartyContact | null;
    address: CounterpartyAddress | null;
}

interface Props {
    show: boolean;
    customerName: string;
    contacts: CounterpartyContact[];
    addresses: CounterpartyAddress[];
    onConfirm: (selection: SelectedCustomerBooks) => void;
    onClose: () => void;
}

function contactSecondaryLine(contact: CounterpartyContact): string {
    const headline = formatCounterpartyContactHeadline(contact);
    return [contact.email, contact.phoneNumber]
        .filter(
            (value): value is string => Boolean(value) && value !== headline,
        )
        .join(" · ");
}

// Shown right after picking a customer whose counterparty has more than one
// contact or address on file: the invoice can only print one of each, so the
// user must choose which contact (email) and which address the invoice — and
// the To fields below the picker — should use. Single-option sections come
// preselected; sections with several options start empty so the choice is
// deliberate.
export const SelectCustomerBooksModal: React.FC<Props> = ({
    show,
    customerName,
    contacts,
    addresses,
    onConfirm,
    onClose,
}) => {
    const [contactId, setContactId] = useState<string | null>(
        contacts.length === 1 ? contacts[0].id : null,
    );
    const [addressId, setAddressId] = useState<string | null>(
        addresses.length === 1 ? addresses[0].id : null,
    );

    const isMissingContact = contacts.length > 0 && contactId === null;
    const isMissingAddress = addresses.length > 0 && addressId === null;
    const isConfirmDisabled = isMissingContact || isMissingAddress;

    const handleConfirm = () => {
        if (isConfirmDisabled) {
            return;
        }
        onConfirm({
            contact: contacts.find(({ id }) => id === contactId) ?? null,
            address: addresses.find(({ id }) => id === addressId) ?? null,
        });
    };

    return (
        <PlainModal show={show} onClose={onClose} size="md">
            <StandardModalHeader>
                Choose contact and address
            </StandardModalHeader>
            <VStack gap={20} className="mb-4">
                <div className={styles.intro}>
                    {customerName} has multiple contacts or addresses. Select
                    the ones to use on this invoice.
                </div>
                {contacts.length > 0 && (
                    <VStack gap={8}>
                        <div className={styles.sectionLabel}>Contact</div>
                        <RadioGroup
                            aria-label="Contact"
                            value={contactId ?? ""}
                            onValueChange={setContactId}
                            className={styles.bookGroup}
                        >
                            {contacts.map((contact) => (
                                <Radio
                                    key={contact.id}
                                    value={contact.id}
                                    className={styles.bookRow}
                                    label={
                                        <span className={styles.option}>
                                            <span
                                                className={styles.optionTitle}
                                            >
                                                {formatCounterpartyContactName(
                                                    contact,
                                                ) ||
                                                    formatCounterpartyContactHeadline(
                                                        contact,
                                                    )}
                                            </span>
                                            {contactSecondaryLine(contact) && (
                                                <span
                                                    className={
                                                        styles.optionDetail
                                                    }
                                                >
                                                    {contactSecondaryLine(
                                                        contact,
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    </VStack>
                )}
                {addresses.length > 0 && (
                    <VStack gap={8}>
                        <div className={styles.sectionLabel}>Address</div>
                        <RadioGroup
                            aria-label="Address"
                            value={addressId ?? ""}
                            onValueChange={setAddressId}
                            className={styles.bookGroup}
                        >
                            {addresses.map((address) => (
                                <Radio
                                    key={address.id}
                                    value={address.id}
                                    className={styles.bookRow}
                                    label={
                                        <span className={styles.option}>
                                            {formatCounterpartyAddressLines(
                                                address,
                                            ).map((line, index) => (
                                                <span
                                                    key={line}
                                                    className={
                                                        index === 0
                                                            ? styles.optionTitle
                                                            : styles.optionDetail
                                                    }
                                                >
                                                    {line}
                                                </span>
                                            ))}
                                        </span>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    </VStack>
                )}
            </VStack>
            <HStack as="footer" gap={12} justify="end">
                <Button variant="secondary" size="md" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    disabled={isConfirmDisabled}
                    onClick={handleConfirm}
                >
                    Use selection
                </Button>
            </HStack>
        </PlainModal>
    );
};
